/* global SeasonPagesEnabled */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import classNames from 'classnames';
import _ from 'lodash';
import { getUrlTarget, PAGE_TYPE_ARTICLE } from 'helpers/ContentHelper';
import { PageNames, getTealiumDetailTabData } from '@foxcorp/delta-web-analytics';
import {
  SITE_PLATFORM_WEB_LARGE,
  SITE_PLATFORM_WEB,
  SITE_PLATFORM_TABLET,
  SITE_PLATFORM_MOBILE,
} from 'helpers/BreakpointHelper';
import VideosTabComponent from 'screens/ContentDetail/components/VideosTab';
import {
  checkForDOMNode,
  clearFetchNodeInterval,
  getSelectedTabIndex,
  getTabDataUrl,
  getTabName,
  getTabSlug,
  getTabUrl,
  hasChildren,
  isSeasonPanel,
  PANEL_TYPE_CLIPS,
} from 'helpers/SeriesDetailHelpers';
import Style from './Tabs.module.scss';
import StyleSeriesDetail from '../../styles/SeriesDetail.module.scss';

import TabListMobile from '../TabListMobile';

const BREAKPOINTS = {
  [SITE_PLATFORM_WEB_LARGE]: {
    tabListSidebar: true,
    scrollOffsetTop: 250,
    scrollOffsetTopArticle: 300,
  },
  [SITE_PLATFORM_WEB]: {
    tabListSidebar: true,
    scrollOffsetTop: 250,
    scrollOffsetTopArticle: 300,
  },
  [SITE_PLATFORM_TABLET]: {
    tabListSidebar: true,
    scrollOffsetTop: 450,
    scrollOffsetTopArticle: 300,
  },
  [SITE_PLATFORM_MOBILE]: {
    tabListSidebar: false,
    scrollOffsetTop: 250,
    scrollOffsetTopArticle: 250,
  },
};

class TabList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selected: null,
    };
    this.checkForDOMNode = checkForDOMNode.bind(this);
    this.clearFetchNodeInterval = clearFetchNodeInterval.bind(this);
  }

  componentDidMount() {
    const { tabs } = this.props;
    const { tabIndex } = this.getCurrentTabIndex();
    let selected = null;

    if (tabIndex !== false && tabIndex !== null) {
      selected = _.get(tabs, `${tabIndex}.id`, null);
    }

    this.setState({ selected });
    this.renderSelected(selected);
  }

  getCurrentTabIndex() {
    const pageId = this.getPageId();
    const { tabs } = this.props;

    if (!tabs) return false;

    const { tabIndex, subTabIndex } = getSelectedTabIndex({
      pageId,
      tabs,
    });
    return { tabIndex, subTabIndex };
  }

  getPageId() {
    const { pageId, pageType } = this.props;
    if (pageType && pageId) return `${pageType}/${pageId}`;
    return pageId || pageType;
  }

  renderTabContent({ tabs, tabIndex, subTabIndex, isAuthenticated, preauthorizedResources }) {
    const { selected } = this.state;
    if (tabIndex === false) return null;
    let item = tabs[tabIndex];
    if (!item) return null;
    if (subTabIndex !== false) item = _.get(item, `children.${subTabIndex}`, item);
    const tabType = _.get(item, '@type', null);

    const selectedTab = _.find(tabs, { id: selected });

    const type = _.get(selectedTab, '@type', null);
    if (type !== tabType) return null;
    const renderedTab = this.renderSingleTabContent({
      item: selectedTab,
      isAuthenticated,
      preauthorizedResources,
      StyleSeriesDetail,
    });
    return renderedTab;
  }

  renderSingleTabContent({ item, isAuthenticated, preauthorizedResources }) {
    const {
      accessToken,
      bookmarks,
      breakpoint,
      collectionCount,
      inline,
      isSeriesDetailPage,
      seriesData,
      setOverlay,
      showUpgradeCTA,
      tabs,
      verticalPosition,
    } = this.props;
    const { scrolling } = this.state;
    let url = getTabDataUrl(item);
    const originalVideos = _.get(item, 'items.member', null);
    const videos =
      originalVideos &&
      originalVideos.map(video =>
        Object.assign({ isAuthenticated, preauthorizedResources }, video),
      );
    const videoCount = _.get(item, 'items.totalItems', null);
    if (videos) {
      if (videoCount === 0) return null;
      url = null;
    }

    return (
      <VideosTabComponent
        accessToken={accessToken}
        bookmarks={bookmarks}
        collectionCount={collectionCount}
        breakpoint={breakpoint}
        inline={inline}
        isAuthenticated={isAuthenticated}
        key={url || item.id}
        preauthorizedResources={preauthorizedResources}
        scrolling={scrolling}
        seriesData={seriesData}
        showUpgradeCTA={showUpgradeCTA}
        isSeriesDetailPage={isSeriesDetailPage}
        setOverlay={setOverlay}
        tab={item}
        tabName={_.get(item, 'name')}
        tabs={tabs}
        url={url}
        verticalPosition={verticalPosition}
        videos={videos}
      />
    );
  }

  renderSelectedTab = selected => {
    this.setState({ selected });
  };

  renderTabs = () => {
    const { pageType, tabs, isAuthenticated, preauthorizedResources } = this.props;

    if (!tabs) return null;

    const { tabIndex, subTabIndex } = this.getCurrentTabIndex();

    let tabContent = null;

    if (!tabContent) {
      tabContent = this.renderTabContent({
        tabs,
        tabIndex,
        subTabIndex,
        isAuthenticated,
        preauthorizedResources,
        pageType,
      });
    }

    return (
      <div style={{ width: '100%', display: 'inline-block', verticalAlign: 'top' }}>
        {tabContent}
      </div>
    );
  };

  createTabs({ tabs, isSubmenu }) {
    const {
      breakpoint,
      pageViewEvent,
      seriesData,
      seriesType,
      showId,
      tabIndex,
      utagSeriesType,
    } = this.props;
    const { selected } = this.state;

    const listLinks = [];
    const slugs = [];
    const tabList = [];
    const playlistLinks = [];
    const videoContentList = [];
    if (tabs) {
      tabs.forEach((tab, idx) => {
        if (!tab) return null;
        const { id: tabId, fullEpisodeCount } = tab;
        const tabType = _.get(tab, '@type', null);
        const isActiveTab = !this.isArticleWithoutTab() && selected === tabId;
        const hasEpisodes = fullEpisodeCount > 0;
        const tabClassActive = isActiveTab ? Style.active : '';
        const tabClasses = classNames(Style.tab, tabClassActive);
        let name = getTabName({ tab, seriesType });

        if (breakpoint !== SITE_PLATFORM_MOBILE && name?.includes('Season')) {
          name = name.replace('Season', '');
        }
        const url = getTabUrl({ showId, tab, tabs, seriesType, isSubmenu });
        const slug = getTabSlug({ tab, tabs, seriesType });
        const { target, rel } = getUrlTarget(url);

        const onClick = () => {
          this.tabClick({ slug, tab });
        };
        const elementId = slug || tabId;
        if (elementId) {
          slugs.push(elementId);
        }

        const onClickAnalytics = () => {
          const { pageTitle, selectedMenuItem } = getTealiumDetailTabData({
            seriesData,
            tab,
          });
          pageViewEvent({
            pageName: PageNames.DETAIL,
            seriesType: utagSeriesType,
            pageTitle,
            selectedMenuItem,
          });
        };
        // Check if tab should be skipped here:
        if (tabType === PANEL_TYPE_CLIPS) return null;

        if (hasChildren(tab)) {
          _.forEach(_.get(tab, 'children'), childTab => {
            const childSlug = getTabSlug({ tab: childTab, tabs, seriesType });
            const childUrl = getTabUrl({ showId, tab: childTab, tabs, seriesType });
            const childOnClick = () => {
              this.tabClick({ slug: childSlug, tab: childTab });
            };
            const childName = _.get(childTab, 'headline');
            tabList.push({
              id: _.get(childTab, 'id'),
              parentName: name,
              name: childName,
              tab: childTab,
              tabClasses,
              linkTo: childUrl,
              onClick: childOnClick,
            });
          });
        } else if (!isSeasonPanel(tab) || hasEpisodes) {
          tabList.push({
            id: tabId,
            name,
            tab,
            tabClasses,
            linkTo: url,
            onClick,
          });
        }

        if (SeasonPagesEnabled && isSeasonPanel(tab) && hasEpisodes) {
          const isSelected = tabIndex === idx;
          videoContentList.push({
            linkTo: url,
            name,
            onClick: () => {
              onClick();
              onClickAnalytics();
            },
            isSelected,
          });
        } else if ((onClick && !isSeasonPanel(tab)) || !SeasonPagesEnabled) {
          const item = (
            <Link
              key={`link-${tabId}`}
              className={tabClasses}
              to={url}
              target={target}
              rel={rel}
              onClick={e => {
                onClick(e);
                onClickAnalytics();
              }}
            >
              {name}
            </Link>
          );
          listLinks.push(item);
        }

        return null;
      });
    }
    return {
      listLinks,
      playlistLinks,
      videoContentList,
      slugs,
      tabList,
    };
  }

  isArticleWithoutTab() {
    const { pageType, tabIndex } = this.props;
    const isArticleWithoutTab = tabIndex === false && pageType === PAGE_TYPE_ARTICLE;
    return isArticleWithoutTab;
  }

  tabClick({ tab }) {
    const { id: tabId } = tab;

    this.setState({ selected: tabId });
    this.renderSelectedTab(tabId);
  }

  renderDesktopTablist = ({ listLinks }) => <div>{listLinks}</div>;

  isSeason = tabs => {
    let isSeason = false;
    if (tabs) {
      for (let i = 0; i < tabs.length; i += 1) {
        if (isSeasonPanel(tabs[i])) {
          isSeason = true;
        }
      }
    }

    return isSeason;
  };

  seasonCount = tabs => _.filter(tabs, tab => isSeasonPanel(tab)).length;

  getHeadline = ({ headline, seasonNumber }) => headline || `Season ${seasonNumber}`;

  renderSelected = tab => {
    this.renderSelectedTab(tab);
  };

  render() {
    const { breakpoint, pageId, overlay, setOverlay, tabIndex, tabs, theme } = this.props;
    const { selected } = this.state;

    const { tabListSidebar } = BREAKPOINTS[breakpoint];

    const { listLinks, playlistLinks, videoContentList, slugs, tabList } = this.createTabs({
      tabs,
    });

    const display = this.isSeason(tabs) ? 'block' : 'none';
    let tabListComponent = null;
    if (tabListSidebar) {
      if (this.seasonCount(tabs) > 1) {
        tabListComponent = this.isSeason(tabs) && (
          <div className={Style.container}>
            <div style={{ float: 'left', paddingRight: 10, display }}>Season</div>{' '}
            {this.renderDesktopTablist({
              listLinks,
              playlistLinks,
              videoContentList,
              slugs,
            })}
          </div>
        );
      } else {
        tabListComponent = this.isSeason(tabs) && (
          <div className={Style.container}>{this.getHeadline(tabs[0])}</div>
        );
      }
    } else {
      tabListComponent = !_.isEmpty(tabList) && this.isSeason(tabs) && (
        <TabListMobile
          tabs={tabList}
          overlay={overlay}
          setOverlay={setOverlay}
          tabIndex={tabIndex}
          pageId={pageId}
          selected={selected}
        />
      );
    }

    const containerClassNames = classNames([theme.tabList, !tabListComponent && theme.noTabs]);
    return (
      <div className={classNames(['container', StyleSeriesDetail.screenElement])}>
        <div className={containerClassNames}>{tabListComponent}</div>
        {this.isSeason(tabs) && this.renderTabs()}
      </div>
    );
  }
}

TabList.defaultProps = {
  accessToken: null,
  collectionCount: null,
  inline: [],
  isAuthenticated: false,
  isSeriesDetailPage: false,
  overlay: null,
  pageId: null,
  pageType: null,
  pageViewEvent: () => {},
  preauthorizedResources: [],
  seriesData: {},
  seriesType: null,
  showUpgradeCTA: false,
  tabIndex: false,
  theme: {},
  utagSeriesType: '',
};

TabList.propTypes = {
  accessToken: PropTypes.string,
  bookmarks: PropTypes.shape({}),
  breakpoint: PropTypes.string.isRequired,
  collectionCount: PropTypes.number,
  inline: PropTypes.shape({}),
  isAuthenticated: PropTypes.bool,
  isSeriesDetailPage: PropTypes.bool,
  overlay: PropTypes.string,
  pageId: PropTypes.string,
  pageType: PropTypes.string,
  pageViewEvent: PropTypes.func,
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  seriesData: PropTypes.shape({}),
  seriesType: PropTypes.string,
  setOverlay: PropTypes.func.isRequired,
  showId: PropTypes.string.isRequired,
  showUpgradeCTA: PropTypes.bool,
  tabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  tabs: PropTypes.arrayOf(PropTypes.shape([])).isRequired,
  theme: PropTypes.shape({}),
  utagSeriesType: PropTypes.string,
  verticalPosition: PropTypes.number,
};

export default TabList;
